import { Col, Form, Select } from "antd";
import { useCallback, useEffect, useState } from "react";
import debounce from "lodash.debounce";
import {
  PO_MAP_NEW_ENDPOINT,
  GET_DIGITALIZATION_SUB_STYLES,
  GET_DIGITALIZATION_STYLES,
  SEWING_LINES,
} from "../../../apiServices/API_ENDPOINTS";
import { getData } from "../../../apiServices/common";
import { isArrayAndHasValue } from "../../../utils/functions";

const FinishingSewingToFoldingFilter = ({ initialTableData }) => {
  // States
  const [sewingList, setSewingList] = useState([]);
  const [buyersList, setBuyersList] = useState([]);
  const [stylesList, setStylesList] = useState([]);
  const [poList, setPOList] = useState([]);
  const [subStyleList, setSubStyleList] = useState([]);

  // Antd
  const { Option } = Select;
  const styleIdWatch = Form.useWatch("style_id");

  const getSewingList = async (filterValues) => {
    const bodyData = {
      per_page: filterValues?.per_page || 10,
      ...filterValues,
    };
    const response = await getData(SEWING_LINES, false, bodyData);

    if (response && response?.data?.code === 200) {
      setSewingList(response?.data?.data?.data || []);
    }
  };

  const getBuyersList = async (filterValues) => {
    const bodyData = {
      per_page: filterValues?.per_page || 10,
      ...filterValues,
    };
    const response = await getData(
      "/api/digitalization/buyers",
      false,
      bodyData,
    );

    if (response && response?.data?.code === 200) {
      const buyers =
        response?.data?.data?.data?.map((buyer) => ({
          id: buyer.id,
          name: buyer.name,
        })) || [];
      setBuyersList(buyers);
    }
  };

  const getStyleList = async (filterValues) => {
    const bodyData = {
      per_page: filterValues?.per_page || 10,
      ...filterValues,
    };
    const response = await getData(GET_DIGITALIZATION_STYLES, false, bodyData);

    if (response && response?.data?.code === 200) {
      const styles =
        response?.data?.data?.data?.map((style) => ({
          id: style.id,
          name: style.style_no,
        })) || [];
      setStylesList(styles);
    }
  };

  const getSubStyleList = useCallback(
    debounce(async (filterValues = {}) => {
      const bodyData = {
        per_page: 10,
        page: 1,
        ...filterValues,
      };
      const response = await getData(
        GET_DIGITALIZATION_SUB_STYLES,
        false,
        bodyData,
      );

      if (response && response?.data?.code === 200) {
        setSubStyleList(response?.data?.data?.data || []);
      }
    }, 300),
    [],
  );

  const getPOList = async (filter = null) => {
    const originalPo =
      typeof filter === "string" ? filter : filter?.original_po || null;

    const bodyData = {
      ...(originalPo && { original_po: originalPo }),
      page: 1,
      per_page: 100,
    };
    const response = await getData(PO_MAP_NEW_ENDPOINT, false, bodyData);

    if (response && response?.data?.code === 200) {
      const data = response?.data?.data?.data || response?.data?.data || [];
      setPOList(data);
    }
  };

  const debouncedGetPOList = useCallback(
    debounce((value) => {
      getPOList({ original_po: value });
    }, 300),
    [],
  );

  // Effects
  useEffect(() => {
    getSewingList();
    getBuyersList();
    getStyleList();
    getSubStyleList();
    getPOList();
  }, []);

  return (
    <>
      <Col span={4}>
        <Form.Item label="Line" name="line_id">
          <Select
            showSearch
            placeholder="Select a Line"
            onSearch={(value) => {
              getSewingList({ name: value });
            }}
            optionFilterProp="children"
            size="small"
            filterOption={false}
          >
            {isArrayAndHasValue(sewingList) &&
              sewingList.map((sup) => (
                <Option value={sup.id} key={sup.id}>
                  {sup.name}
                </Option>
              ))}
          </Select>
        </Form.Item>
      </Col>

      <Col span={4}>
        <Form.Item label="Style" name="style_id">
          <Select
            showSearch
            placeholder="Select Style"
            filterOption={false}
            size="small"
            onSearch={(value) => {
              getStyleList({ style_no: value });
            }}
          >
            {isArrayAndHasValue(stylesList) &&
              stylesList.map((sup) => (
                <Option value={sup.id} key={sup.id}>
                  {sup.name}
                </Option>
              ))}
          </Select>
        </Form.Item>
      </Col>

      <Col span={4}>
        <Form.Item label="Sub Style" name="sub_style_id">
          <Select
            showSearch
            placeholder="Search Sub Style"
            filterOption={false}
            size="small"
            allowClear
            onClear={() => getSubStyleList()}
            onSearch={(value) =>
              getSubStyleList({
                sub_style_no: value,
                style_id: styleIdWatch || null,
              })
            }
          >
            {isArrayAndHasValue(subStyleList) &&
              subStyleList.map((item) => (
                <Option value={item.id} key={item.id}>
                  {item.sub_style_no}
                </Option>
              ))}
          </Select>
        </Form.Item>
      </Col>

      <Col span={4}>
        <Form.Item label="Original PO" name="po_map_id">
          <Select
            showSearch
            placeholder="Select an original PO"
            onSearch={(value) => {
              debouncedGetPOList(value);
            }}
            optionFilterProp="children"
            filterOption={(input, option) =>
              String(option?.children ?? "")
                .toLowerCase()
                .includes(String(input ?? "").toLowerCase())
            }
            size="small"
            allowClear
            onClear={() => getPOList()}
          >
            {isArrayAndHasValue(poList) &&
              poList.map((po) => (
                <Option value={po.id} key={po.id}>
                  {po.original_po}
                </Option>
              ))}
          </Select>
        </Form.Item>
      </Col>
    </>
  );
};

export default FinishingSewingToFoldingFilter;
